import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { AccessData, ContactInfo } from "../models/accessData.model";
import { BehaviorSubject, firstValueFrom, lastValueFrom, Subject } from "rxjs";
import * as jwt_decode from "jwt-decode";
import { CompteEntrepriseService } from "./compteEntreprise.service";
import { CompanyDetails } from "../models/compteEntreprise.model";
import { FormulaireDonneePersoService } from "./formulaireDonneePerso.service";
import { InfoPerso } from "../models/InfoPerso.model";
import { AccountItem } from "../models/accessData.model";
import { COUNTRIES } from "../../../config/countries";
import { ListeService } from "./liste.service";
import { AllListLabelValue, LabelValue } from "../models/liste.model";
import { SettingService } from "./settings.service";
import { EcliSettings } from "../models/settings.model";
import {
  GS1LoadingService,
  GS1NotificationService,
} from "@gs1france/gs1-components-library";
import { Invoice } from "../models/Invoice.model";
import { InvoiceService } from "./invoice.service";
import { TranslateService } from "@ngx-translate/core";
import { MandatService } from "./mandat.service";
import { AllSEPAMandates } from "../models/Mandate.model";
import { AccessDataFromToken } from "../models/AccessDataFromToken.model";
import {
  AccessDataByEmailInputModel,
  AccountModel,
  PrivilegeModel,
} from "../models/accessDataByEmailInput.model";
import { OAuthService } from "angular-oauth2-oidc";
import { JwtHelperService } from "@auth0/angular-jwt";

export type ListName =
  | "Activite"
  | "ContributionBracket"
  | "Departements"
  | "Job"
  | "LegalForms"
  | "MembershipStatuses"
  | "NAF"
  | "SalesChannel"
  | "Packs"
  | "Products"
  | "Marketings";
@Injectable({ providedIn: "root" })
export class StorageService {
  hasStorage: true;
  itemStorageList = [
    "accessData",
    "multiple",
    "tokenGs1",
    "session",
    "nomComplet",
  ];
  storage: Storage;
  accessDataValue: AccessData;
  accessData: BehaviorSubject<AccessData> = new BehaviorSubject<AccessData>(
    null
  );
  tokenGS1Value: any;
  tokenGS1: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  multipleValue: boolean;
  multiple: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  sessionValue: any;
  session: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  nomCompletValue: string;
  nomComplet: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  companyValue: CompanyDetails;
  company: Subject<CompanyDetails> = new Subject<CompanyDetails>();
  private loadingCompany = false;
  loadingUser = false;
  userValue: InfoPerso;
  user: Subject<InfoPerso> = new Subject<InfoPerso>();
  contactInfoValue: ContactInfo;
  contactInfo: BehaviorSubject<ContactInfo> = new BehaviorSubject<ContactInfo>(
    null
  );
  listsValue: AllListLabelValue;
  lists: Subject<boolean> = new Subject<boolean>();
  listLoading = false;
  accountValue: AccountItem;
  account: BehaviorSubject<AccountItem> = new BehaviorSubject<AccountItem>(
    null
  );
  settingsValue: EcliSettings;
  settings: Subject<EcliSettings> = new Subject<EcliSettings>();
  settingsLoading = false;
  packValue: string;
  pack: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private loadingPack = false;
  unpaidInvoicesValue: { count: number; toPay: Invoice[]; autoPay: Invoice[] };
  unpaidInvoices: BehaviorSubject<{
    count: number;
    toPay: Invoice[];
    autoPay: Invoice[];
  }> = new BehaviorSubject(null);
  private loadingUnpaid = false;
  mandatValue: AllSEPAMandates;
  mandat: BehaviorSubject<AllSEPAMandates> = new BehaviorSubject(null);
  private loadingMandat = false;
  decodedTokenValue: AccessDataFromToken;
  decodedToken: BehaviorSubject<AccessDataFromToken> =
    new BehaviorSubject<AccessDataFromToken>(null);
  searchTextValue: string;
  searchText: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  //is redirected to sign cgu or compelete information
  isRedirected = false;
  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    private _compteEntrepriseService: CompteEntrepriseService,
    private _userService: FormulaireDonneePersoService,
    private _mandate: MandatService,
    private _listeService: ListeService,
    private _invoiceService: InvoiceService,
    private _loadingService: GS1LoadingService,
    private _notifService: GS1NotificationService,
    private _settingsService: SettingService,
    private _translate: TranslateService,
    public authService: OAuthService
  ) {
    this.storage = localStorage;
  }

  public read(key: string): any {
    if (isPlatformBrowser(this.platformId)) {
      let str = this.storage.getItem(key);
      return str ? JSON.parse(str) : null;
    } else {
      return null;
    }
  }

  public readRaw(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      let str = this.storage.getItem(key);
      return str;
    } else {
      return null;
    }
  }

  public write(key: string, value: any): void {
    value = value === undefined ? null : value;
    this.storage.setItem(key, JSON.stringify(value));
  }

  public writeRaw(key: string, value: string): void {
    this.storage.setItem(key, value);
  }

  resetStorage() {
    this.removeCompanyData();
    this.setAccount(null);
    this.setPack(null);
    this.setAccountInfo(null);
    this.setUser(null);
  }

  public setAccessData(value: AccessData) {
    this.accessDataValue = value;
    this.accessData.next(this.accessDataValue);
  }

  public removeAccessData() {
    this.accessDataValue = null;
    this.accessData.next(null);
  }

  public removeCompanyData() {
    this.removeAccessData();
    this.removeUnpaidInvoices();
    this.companyValue = null;
  }

  public updateUserWithName(firstName: string, lastName: string) {
    this.userValue.prenom = firstName;
    this.userValue.nom = lastName;
    this.setNomComplet(firstName, lastName);
    this.user.next(this.userValue);
  }

  public updateAccessDataWithName(firstName: string, lastName: string) {
    this.accessDataValue.contactFirstName = firstName;
    this.accessDataValue.contactLastName = lastName;
    this.setNomComplet(firstName, lastName);
    this.accessData.next(this.accessDataValue);
  }
  public setSearchText(value: string) {
    this.searchTextValue = value;
    this.searchText.next(value);
    this.write("searchText", value);
  }

  public getSearchText(): string {
    if (!this.searchTextValue) {
      this.searchTextValue = this.read("searchText");
    }
    return this.searchTextValue;
  }

  public clearSearchText() {
    this.searchTextValue = null;
    this.searchText.next(null);
    this.storage.removeItem("searchText");
  }

  public async setAccountInfo(value: any) {
      this.setSession(value);
      this.writeRaw("accountInfo", JSON.stringify(value) || "");
      this.retrieveStaticLists();

  }
  //to delete
  public getGS1Token() {
    const token = this.tokenGS1Value || this.readRaw("access_token");
    const account = this.accountValue
      ? this.accountValue
      : <AccountItem>JSON.parse(this.readRaw("accountInfo"));
    if (token && token !== "") {
      const decodedToken = jwt_decode(token);
      const valid = decodedToken.exp * 1000 > Date.now();
      if (!this.tokenGS1Value && valid) {
        //this.setGS1Token(token);
        if (account) {
          this.setAccount(account);
        }
      }
      return valid ? this.tokenGS1Value : "";
    }
    return "";
  }

  private setSettings(settings: EcliSettings) {
    this.settingsValue = settings;
    this.settings.next(this.settingsValue);
  }
  public async getSettings(): Promise<EcliSettings> {
    if (this.settingsLoading) {
      return firstValueFrom(this.settings);
    }
    if (!this.settingsValue) {
      this.settingsLoading = true;
      const settings = await this._settingsService.getSettings();
      this.setSettings(settings);
      this.settingsLoading = false;
    }
    return this.settingsValue;
  }

  async retrieveStaticLists() {
    this._loadingService.startLoading("lists");
    const lists = await this._listeService.getAllLists();
    this.setStaticLists(lists);
    this._loadingService.stopLoading("lists");
  }
  private setStaticLists(lists: AllListLabelValue) {
    this.listsValue = lists;
    this.lists.next(true);
  }

  setUser(user: InfoPerso) {
    this.userValue = user;
    this.user.next(this.userValue);
  }

  public async getUser(): Promise<InfoPerso> {
    if (this.loadingUser) {
      return await firstValueFrom(this.user);
    }
    if (!this.userValue) {
      this.loadingUser = true;
      const user = await this._userService.getInfoPerso();
      this.setUser(user);
      this.loadingUser = false;
    }
    return this.userValue;
  }

  private setAccount(account: AccountItem) {
    this.accountValue = account;
    this.account.next(this.accountValue);
    this.setSession(account);
  }

  private setContact(contact: ContactInfo) {
    this.contactInfoValue = contact;
    this.contactInfo.next(this.contactInfoValue);
  }

  public setSession(value: any) {
    this.sessionValue = value;
    if (!this.nomCompletValue) {
      this.setNomComplet(
        JSON.parse(this.sessionValue["contact_info"]).FirstName,
        JSON.parse(this.sessionValue["contact_info"]).LastName
      );
    }

    this.session.next(this.sessionValue);
  }
  public getSession() {
    return this.sessionValue;
  }

  private setMultiple(value: boolean) {
    this.multipleValue = value;
    this.multiple.next(this.multipleValue);
  }
  public getMultiple() {
    if (!this.multipleValue && this.multipleValue !== false) {
      this.getAccessData();
    }
    return this.multipleValue;
  }

  public setNomComplet(firstName: string, lastName: string) {
    this.nomCompletValue = firstName + " " + lastName;
    this.nomComplet.next(this.nomCompletValue);
  }
  public getNomComplet() {
    return this.nomCompletValue;
  }

  public setCompany(company: CompanyDetails) {
    
    this.companyValue = company;
    if (company && company.countryCode)
      this.companyValue.country = COUNTRIES.filter(
        (country: any) =>
          country.alpha2Code.toLowerCase() === company.countryCode.toLowerCase()
      )[0].name;
    this.company.next(this.companyValue);
  }
  public async getCompany(): Promise<CompanyDetails> {
    if (!this.companyValue) {
      return await firstValueFrom(this.company);
    }
    return this.companyValue;
  }
  public async getCompanyDisplayedSections(): Promise<{
    activateCA: boolean;
    activateSEPA: boolean;
    activateLogo: boolean;
    activatePacks: boolean;
    hasCustomerNumber: Boolean;
  }> {
    try {
      const [company] = await Promise.all([this.getCompany()]);
      if (company) {
        return {
          activateCA:
            !company.activite ||
            (!company.activite.label.includes("santé") &&
              !company.activite.label.includes("Administration") &&
              !company.activite.label.includes("bancaire")),
          activateSEPA:
            company.siren &&
            (company.countryCode?.toUpperCase() === "FR" ||
              company.countryCode?.toUpperCase() === "FR-TOM" ||
              company.countryCode?.toUpperCase() === "FR-DOM"),
          activateLogo:
            company.typeCompte === "GS1 Partenaires" &&
            company.packId !== "DD6B09B2-4124-4D6C-822B-F3102BC49835",
          activatePacks: company.typeCompte === "GS1 Partenaires",
          hasCustomerNumber:
            company.customerNumber !== null &&
            company.customerNumber?.trim()?.length > 0,
        };
      } else {
        throw new Error();
      }
    } catch {
      return {
        activateCA: false,
        activateSEPA: false,
        activateLogo: false,
        activatePacks: false,
        hasCustomerNumber: false,
      };
    }
  }
  public async refreshAndGetCompany() {
    this.companyValue = null;
    const company = await this._compteEntrepriseService.getCompanyDetails();
    this.setCompany(company);
    return await this.getCompany();
  }

  public setPack(pack: string) {
    this.packValue = pack;
    this.pack.next(this.packValue);
  }
  public async getAccessData(): Promise<AccessData> {
    if (!this.accessDataValue) {
      const accessData = new AccessDataByEmailInputModel();
      accessData.account = new AccountModel();
      var token = this.getDecodedToken();
      accessData.account.companyId = token.companyId;
      accessData.account.name = token.companyName;
      if (token.privileges instanceof Array) {
        accessData.account.privileges = token.privileges.map(
          (x) => new PrivilegeModel(x)
        );
      } else {
        accessData.account.privileges.push(
          new PrivilegeModel(token.privileges)
        );
      }
      this._loadingService.startLoading("access-data");
      this.setMultiple(this.decodedTokenValue['companies_nb'] > 1);
      this._loadingService.stopLoading("access-data");
    }
    return this.accessDataValue;
  }

  public async getListLabelValue(name: ListName): Promise<LabelValue[]> {
    if (!this.listsValue) {
      await firstValueFrom(this.lists);
      return this.listsValue.values[name];
    } else {
      return this.listsValue.values[name];
    }
  }

  public async getAllListLabelValue(): Promise<AllListLabelValue> {
    if (!this.listsValue) {
      await firstValueFrom(this.lists);
      return this.listsValue;
    } else {
      return this.listsValue;
    }
  }
  public setDecodedToken(token: AccessDataFromToken) {
    this.decodedTokenValue = token;
    this.decodedToken.next(this.decodedTokenValue);
    this.setAccountInfo(token);
  }

  public getDecodedToken(): AccessDataFromToken {
    if (this.decodedTokenValue) {
      return new AccessDataFromToken(this.decodedTokenValue);
    } else {
      if (this.authService.hasValidAccessToken()) {
            const token = this.authService.getAccessToken();
        const jwtHelper = new JwtHelperService();
         const decodedToken = jwtHelper.decodeToken(token) as AccessDataFromToken;
        this.setDecodedToken(decodedToken);
        return decodedToken;
    }
  }
  }

  public async getUnpaidInvoices(forceReload: boolean = false) {
    if (this.loadingUnpaid) {
      return await firstValueFrom(this.unpaidInvoices);
    }
    if (!this.unpaidInvoicesValue || forceReload) {
      this.loadingUnpaid = true;
      try {
        const [invoices, mandat] = await Promise.all([
          lastValueFrom(
            this._invoiceService.getDetailInvoices("invoiceunpaid", 0, 2000)
          ),
          this.getMandat(),
        ]);
        invoices.data.map((invoice) => {
          invoice.type = invoice.type
            ? this._translate.instant("Invoice.Types." + invoice.type)
            : "";
          return invoice;
        });
        // if (!mandat.CurrentMandate) {
        //     this.setUnpaidInvoices(invoices.count, invoices.data, []);
        // } else {
        this.setUnpaidInvoices(
          invoices.count,
          invoices.data.filter((invoice) => invoice.inDebit !== true),
          invoices.data.filter((invoice) => invoice.inDebit === true)
        );
        //}
      } catch (err: any) {
        if (!(err && err.status && err.status === 404)) {
          this._notifService.addNotification(
            "Vos factures impayées n'ont pu être récupérées",
            "error"
          );
        } else {
          this.setUnpaidInvoices(0, [], []);
        }
      } finally {
        this.loadingUnpaid = false;
      }
    }
    return this.unpaidInvoicesValue;
  }

  setUnpaidInvoices(count: number, toPay: Invoice[], autoPay: Invoice[]) {
    this.unpaidInvoicesValue = { count, toPay, autoPay };
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  removeUnpaidInvoices() {
    this.unpaidInvoicesValue = null;
    this.unpaidInvoices.next(this.unpaidInvoicesValue);
  }

  public async getMandat() {
    if (this.loadingMandat) {
      return await firstValueFrom(this.mandat);
    }
    if (!this.mandatValue) {
      this.loadingMandat = true;
      try {
        const mandates = await this._mandate.getMandates();
        this.setMandate(mandates);
      } catch (err: any) {
        if (!(err && err.status && err.status === 404)) {
          this._notifService.addNotification(
            "Vos mandats de prélèvement n'ont pu être récupérés",
            "error"
          );
        } else {
          this.setMandate({ Mandates: [], CurrentMandate: null });
        }
      } finally {
        this.loadingMandat = false;
      }
    }
    return this.mandatValue;
  }

  setMandate(mandates: AllSEPAMandates) {
    this.mandatValue = mandates;
    this.mandat.next(this.mandatValue);
  }

  public remove(item: string) {
    this.storage.removeItem(item);
  }

  public clearStorage() {
    this.itemStorageList.forEach((item) => this.storage.removeItem(item));
    this.resetStorage();
    this.storage.clear();
  }

  public isUserAdmin() {
    return (
      this.userValue &&
      this.userValue.roles &&
      this.userValue.roles.includes("administrateur")
    );
  }
  public async isUserFederation() {
    await this.getUser();

    return (
      this.userValue &&
      this.userValue.roles &&
      this.userValue.roles.length == 1 &&
      this.userValue.roles.includes("Fédération")
    );
  }

  public preserveConnexion() {
    this.writeRaw("gs1-token", this.getGS1Token());
  }
}
